<script>
    import { onMount, tick } from "svelte";
    import Sidebar from "../_sidebar/index.svelte";
    import AssetsApi from "../asset/assets.api.js";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import PropertiesList from "../../components/properties-list.svelte";
    import PropertiesWidget from "../../components/properties-detail-widget.svelte";
    import {
        ipAdressesCustomComparator,
        locationCustomComparator,
    } from "../../js/utils.js";
    import powerTableService from "../../services/powerTable.js";
    import PropValueRow from "../../components/powerTable/prop-value-row.svelte";
    import AdvancedSearch from "../../components/powerTable/advanced-search/index.svelte";
    import IssuerTopFive from "./_components/issuerTopFive.svelte";
    import CertsByAssetKind from "./_components/certsByAssetKind.svelte";
    import { goto, params } from "@roxi/routify";
    import dayjs from "dayjs";
    import { format, locale } from "svelte-i18n";
    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import "dayjs/locale/es";
    import "dayjs/locale/en";
    import { token } from "../user/user.state.svelte";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    $goto; // manually initialize the helper
    $params;

    // Reactive filter state bound directly to URL params
    let currentFilter = $derived($params ? $params : {});
    let refreshTrigger = $state(0);

    let data = [];
    let items = [];
    let show_props_modal = $state(false);
    let certDetail = $state([]);
    let propertiesModal;
    let modalTitle = $state();
    let allProps;
    let infoBtns;

    const getDateLabels = (date) => {
        console.log(date, "date")
        let expiration_date;
        let expired_label;
        let now = dayjs();

        if (dayjs(date) < now) {
            let diff = dayjs(date).diff(now, "months");
            expired_label =
                $format("date_labels.from") +
                Math.abs(diff) +
                $format("date_labels.months");

            if (diff == 0) {
                diff = dayjs(date).diff(now, "days");
                expired_label =
                    $format("date_labels.from") +
                    Math.abs(diff) +
                    $format("date_labels.days");
            }
            if (diff > 12) {
                diff = dayjs(date).diff(now, "years");
                expired_label =
                    $format("date_labels.from") +
                    Math.abs(diff) +
                    $format("date_labels.years");
            }
            expiration_date = $format("expired");
        } else {
            expiration_date = dayjs(now).to(date);
        }

        return {
            expiration_date: expiration_date,
            expired_label: expired_label,
        };
    };

    const parseCertificateRows = (pageContent) => {
        pageContent.forEach(async (row) => {
            if (row["subject_cn"]) {
                row["subject_cn"] =
                    `<a href="/property/asset/tls/subject_cn/values?value=${row.subject_cn}">` +
                    row.subject_cn +
                    "</a>" +
                    `&nbsp;&nbsp;<i data-loc="${row.location}" data-cn="${row.subject_cn}" data-asset-id="${row.asset_id}" class="props-info-btn fas fa-info-circle" style="cursor: pointer;"></i>`;
            }
            if (row["subject_an"]) {
                row.subject_an = {
                    kind: "tls",
                    name: "subject_an",
                    value: row.subject_an,
                    target_object_type: "asset",
                    max_chars: 15,
                };
            } else {
                row.subject_an = {
                    kind: "tls",
                    name: "subject_an",
                    value: null,
                    target_object_type: "asset",
                    max_chars: 15,
                };
            }

            if (row["issuer_cn"]) {
                row["issuer_cn"] =
                    `<a href="/property/asset/tls/issuer_cn/values?value=${row.issuer_cn}">` +
                    row.issuer_cn +
                    `</a>`;
            }
            if (row["asset_name"]) {
                row["asset_name"] =
                    `<a href='/asset/${row.asset_kind}/${row.asset_id}/'>${row.asset_name || ""}</a>`;
            }
            if (row["asset_kind"]) {
                row["asset_kind"] = $format(
                    `asset.kind.${row.asset_kind}.singular`,
                );
            }
            if (row["not_before"]) {
                row["not_before"] = dayjs(row["not_before"]).format(
                    "YYYY-MM-DD",
                );
            }
            if (row["not_after"]) {
                row["not_after"] = dayjs(row["not_after"]).format(
                    "YYYY-MM-DD",
                );
            }
            if (row["expiration_date"]) {
                let date = getDateLabels(
                    row["expiration_date"],
                ).expiration_date;
                row["expiration_date"] =
                    date === $format("expired")
                        ? `<span class="vlsk-red">${date}</span>`
                        : date;
            }
        });

        addInfoBtnsEventListeners();

        return pageContent;
    };

    const addInfoBtnsEventListeners = async () => {
        await tick();
        if (infoBtns) {
            for (let btn of infoBtns) {
                btn.removeEventListener("click", showInfoModal);
            }
        }

        infoBtns = document.getElementsByClassName("props-info-btn");
        for (let btn of infoBtns) {
            btn.addEventListener("click", showInfoModal);
        }
    };

    let columns = [
        {
            key: "subject_cn",
            title: $format("list.columns.name"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "subject_an",
            title: "SAN",
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "issuer_cn",
            title: $format("list.columns.issuer"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "location",
            title: $format("list.columns.location"),
            filterable: false,
            userFunctions: { customSort: locationCustomComparator },
        },
        {
            key: "asset_kind",
            title: $format("list.columns.asset_kind"),
            filterable: false,
        },
        {
            key: "asset_name",
            title: $format("list.columns.asset"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: ipAdressesCustomComparator },
        },
        {
            key: "not_before",
            title: $format("list.columns.valid_from"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortDates },
        },
        {
            key: "not_after",
            title: $format("list.columns.valid_until"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortDates },
        },
        {
            key: "expiration_date",
            title: $format("list.columns.expires_at"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: powerTableService.sortDates },
        },
    ];

    const groupPropertiesByLocation = (data, assets, colKeys) => {
        for (let [idx, row] of data.entries()) {
            let { location, name, value, asset_id, ...rest } = row;

            items.push({ location: location, asset_id: asset_id });
            for (let [index, item] of items.entries()) {
                if (
                    row["location"] === item["location"] &&
                    row["asset_id"] === item["asset_id"]
                ) {
                    let itemIdx = items.findIndex(
                        (itm) =>
                            itm["location"] === item["location"] &&
                            itm["asset_id"] === item["asset_id"],
                    );
                    if (itemIdx > -1) {
                        if (Object.keys(colKeys).includes(row["name"])) {
                            for (let k of Object.keys(colKeys)) {
                                if (row["name"] === k) {
                                    items[itemIdx][k] = row.value;
                                    items[itemIdx].asset_name = row.asset_id
                                        ? assets.filter(
                                              (asset) =>
                                                  asset.id === row.asset_id,
                                          )[0]["name"]
                                        : "";
                                    items[itemIdx].asset_kind = row.asset_id
                                        ? assets.filter(
                                              (asset) =>
                                                  asset.id === row.asset_id,
                                          )[0]["kind"]
                                        : "";
                                }
                            }
                        }
                    }
                }
            }
        }
        return items;
    };

    const handleEmptyFilters = (filters) => {
        let clean = { ...filters };
        for (let [key, filter] of Object.entries(clean)) {
            if (filter === "" || filter === null || filter === undefined) {
                delete clean[key];
            }
        }
        return clean;
    };

    const getData = async (config = { filter: {} }) => {
        if (!token.access_token) return {};
        items = [];

        let activeFilter = handleEmptyFilters(config.filter);

        let assetKindIn = activeFilter.asset__kind__in
            ? activeFilter.asset__kind__in
            : "private_ip,public_ip,hostname";

        data = await AssetsApi.fetchAssets("v2/asset-property/", {
            filter: { ...activeFilter, kind: "tls" },
        });
        let assets = await AssetsApi.fetchAssets("v2/asset/", {
            filter: { kind__in: assetKindIn },
        });

        let colKeys = {};
        for (let col of columns) {
            colKeys[col.key] = "";
        }
        items = groupPropertiesByLocation(data, assets, colKeys);
        let columnsDef = Object.keys(colKeys).filter(
            (key) => key != "expiration_date" && key != "subject_an",
        );
        columnsDef.push("asset_id");
        items = items.filter((item) => {
            let itemColumns = Object.keys(item).filter(
                (key) => key != "expiration_date" && key != "subject_an",
            );
            if (
                JSON.stringify(columnsDef.sort()) ===
                JSON.stringify(itemColumns.sort())
            ) {
                item.expiration_date = item.not_after;
                return true;
            }
        });
        items = powerTableService.orderByDate(items);
        let certificatesByAssetKind = getAssetTypeCount(items, assets);
        let issuerTopFive = getIssuerTopFive(items);
        console.log(items, "items")
        return {
            data: items,
            certsByAssetKind: certificatesByAssetKind,
            issuerTopFive: issuerTopFive,
        };
    };

    const getCertificateProps = async (loc, asset_id) =>
        (allProps = await AssetsApi.fetchAssets("v2/asset-property/", {
            filter: { kind: "tls", location: loc, asset_id: asset_id },
        }));

    const orderCertificateDetailProps = (props) => {
        return props.sort((a, b) => a.name.localeCompare(b.name));
    };

    const showInfoModal = async (event) => {
        let { loc, cn, assetId } = event.target.dataset;
        show_props_modal = true;
        let cert = items.find(
            (item) =>
                item["location"] === loc &&
                item["asset_id"] === assetId &&
                item["subject_cn"] === cn,
        );
        certDetail = await getCertificateProps(
            cert["location"],
            cert["asset_id"],
        );
        certDetail = orderCertificateDetailProps(certDetail);
        modalTitle = cert.subject_cn + "-" + cert["location"];
        propertiesModal.modal("show");
    };

    const getAssetTypeCount = (certificates) => {
        const result = certificates.reduce((r, o) => {
            Object.entries(o).forEach((e) => {
                if (e[0] == "asset_kind") {
                    r[e[1]] = (r[e[1]] || 0) + 1;
                }
            });

            return r;
        }, {});

        return result;
    };

    const getIssuerTopFive = (certificates) => {
        const result = certificates.reduce((r, o) => {
            Object.entries(o).forEach((e) => {
                if (e[0] == "issuer_cn") {
                    r[e[1]] = (r[e[1]] || 0) + 1;
                }
            });

            return r;
        }, {});
        let certCount = Object.entries(result).reduce((result, [k, v]) => {
            return { ...result, [k]: v };
        }, {});

        let orderedCertCount = Object.entries(certCount)
            .sort((a, b) => b[1] - a[1])
            .splice(0, 5);
        let finalResult = [];
        for (let v of orderedCertCount) {
            let obj = {};
            obj["id"] = v[0];
            obj["value"] = v[1];
            finalResult.push(obj);
        }
        return finalResult;
    };

    const handleAdvancedSearch = async (event) => {
        let filters = event.detail;
        let updatedFilter = { ...currentFilter };

        for (let [k, v] of Object.entries(filters)) {
            if (Array.isArray(v) && v.length > 0 && v[0] && v[0].length > 0) {
                updatedFilter[k] = v.join();
            } else if (
                v &&
                typeof v === "string" &&
                v.length > 0 &&
                v !== "NaN"
            ) {
                updatedFilter[k] = v;
            } else {
                delete updatedFilter[k];
            }
        }

        const cleanFilters = handleEmptyFilters(updatedFilter);

        // Routify fix: Pass the path and the params object separately
        $goto("/certificate", cleanFilters);
    };

    const handleTableRefresh = async () => {
        refreshTrigger++;
    };

    onMount(() => {
        propertiesModal = jQuery("#properties-detail-modal").modal({
            show: false,
        });
    });
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i
                        class={"fas fa-xs " +
                            AssetsApi.AssetsIcons["certificate"]}
                    ></i>
                    {$format("layout.sidebar.certificates")}
                </h1>
            </div>
            <div class="col-6">
                <BreadCrumNav
                    routes={[{ label: $format("layout.sidebar.certificates") }]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                {#key `${JSON.stringify(currentFilter)}_${refreshTrigger}`}
                    {#await getData({ filter: currentFilter })}
                        <div class="card mt-3" style="min-height: 500px;">
                            <div
                                class="card-body"
                                style="display: flex; justify-content: center;"
                            >
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"
                                    ></i>
                                </div>
                            </div>
                        </div>
                    {:then data}
                        <section class="content">
                            <div class="container-fluid">
                                <div class="row">
                                    <div class="col-12">
                                        <div
                                            class="card mt-3 card-secondary card-outline"
                                        >
                                            <div class="card-header">
                                                <h4 class="card-title">
                                                    {$format(
                                                        "components.advancedSearch.search",
                                                    )}
                                                </h4>
                                            </div>
                                            <div class="card-body pe-5">
                                                <AdvancedSearch
                                                    config={{
                                                        inputs: [
                                                            [
                                                                {
                                                                    type: "multiple",
                                                                    name: "asset__kind__in",
                                                                    id: "assetKindMultiple",
                                                                    label: $format(
                                                                        "list.columns.asset_kind",
                                                                    ),
                                                                    options: [
                                                                        {
                                                                            id: "public_ip",
                                                                            name: $format(
                                                                                "asset.kind.public_ip.singular",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "private_ip",
                                                                            name: $format(
                                                                                "asset.kind.private_ip.singular",
                                                                            ),
                                                                        },
                                                                        {
                                                                            id: "hostname",
                                                                            name: $format(
                                                                                "asset.kind.hostname.singular",
                                                                            ),
                                                                        },
                                                                    ],
                                                                },
                                                            ],
                                                        ],
                                                        activeFilters:
                                                            currentFilter,
                                                    }}
                                                    on:advancedSearch={handleAdvancedSearch}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <div class="d-flex" style="gap: 15px;">
                                            <div class="card mt-3" style="flex: 1;">
                                                <div class="card-header">
                                                    <div class="card-title">
                                                        {$format(
                                                            "page.certificate.components.certs_by_asset_kind",
                                                        )}
                                                    </div>
                                                </div>
                                                <div class="card-body">
                                                    <CertsByAssetKind
                                                        certsCount={data.certsByAssetKind}
                                                        on:advancedSearch={(
                                                            e,
                                                        ) => {
                                                            const newFilters =
                                                                handleEmptyFilters(
                                                                    {
                                                                        ...currentFilter,
                                                                        ...e.detail,
                                                                    },
                                                                );
                                                            $goto(
                                                                window.location
                                                                    .pathname,
                                                                newFilters,
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div class="card mt-3" style="flex: 1;">
                                                <div class="card-header">
                                                    <div class="card-title">
                                                        {$format(
                                                            "page.certificate.components.top_five_issuers",
                                                        )}
                                                    </div>
                                                </div>
                                                <div class="card-body">
                                                    <IssuerTopFive
                                                        certificates={data.issuerTopFive}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="row mt-3">
                                    <div class="col-12">
                                        <PropertiesList
                                            data={data.data}
                                            {columns}
                                            exportsName="certificados"
                                            parseFunc={parseCertificateRows}
                                            on:refresh={handleTableRefresh}
                                            on:showInfoModal={showInfoModal}
                                            certsByAssetKind={data.certsByAssetKind}
                                            issuerTopFive={data.issuerTopFive}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    {/await}
                {/key}
            </div>
        </div>
    </div>
</section>

<!-- MODAL -->
<div
    class="modal fade show"
    id="properties-detail-modal"
    style="display: none; padding-right: 9px;"
    aria-modal="true"
    role="dialog"
>
    <div class="modal-dialog modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h4>{modalTitle}</h4>
                <button
                    type="button"
                    class="close"
                    data-dismiss="modal"
                    aria-label="Close"
                >
                    <span aria-hidden="true">×</span>
                </button>
            </div>
            <div class="modal-body">
                {#if show_props_modal}
                    <PropertiesWidget data={certDetail} />
                {/if}
            </div>
        </div>
        <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
</div>
<!-- END MODAL-->

<style>
</style>
